import { useCallback, useState, useEffect, useRef } from 'react';
import { useLogin } from '../../api/auth';
import styles from './Login.module.css';
import { EyeIcon, EyeOffIcon } from '../../assets/Icons/Icons';

const validateEmail = (email) => {
  if (!email) return 'Email is required';
  if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) return 'Please enter a valid email';
  return '';
};
const validatePassword = (password) => {
  if (!password) return 'Password is required';
  if (password.length < 6) return 'Password must be at least 6 characters';
  return '';
};
const VALIDATORS = { email: validateEmail, password: validatePassword };

const FLIGHT_PATHS = [
  { d: 'M-50,200 Q200,80 500,150 Q700,200 950,100', dur: 18, delay: 0, size: 15, op: 0.6 },
  { d: 'M1000,350 Q700,250 400,300 Q150,340 -50,280', dur: 22, delay: 3, size: 12, op: 0.45 },
  { d: 'M-50,450 Q250,380 550,420 Q800,450 1000,380', dur: 25, delay: 7, size: 10, op: 0.35 },
  { d: 'M500,-50 Q450,150 480,350 Q500,500 550,650', dur: 20, delay: 5, size: 11, op: 0.3 },
  { d: 'M-50,550 Q300,480 600,520 Q850,540 1050,490', dur: 30, delay: 10, size: 8, op: 0.2 },
];

const CITIES = [
  { cx: 150, cy: 180, label: 'LONDON', delay: 0 },
  { cx: 520, cy: 140, label: 'DUBAI', delay: 0.5 },
  { cx: 800, cy: 200, label: 'TOKYO', delay: 1 },
  { cx: 350, cy: 350, label: 'PARIS', delay: 1.5 },
  { cx: 680, cy: 400, label: 'BALI', delay: 0.8 },
  { cx: 100, cy: 420, label: 'NYC', delay: 2 },
  { cx: 900, cy: 350, label: 'SYDNEY', delay: 1.2 },
  { cx: 440, cy: 520, label: '', delay: 0.3 },
  { cx: 250, cy: 500, label: '', delay: 1.8 },
  { cx: 750, cy: 120, label: '', delay: 0.7 },
];

const PARTICLES = Array.from({ length: 20 }, () => ({
  left: `${5 + Math.random() * 90}%`,
  top: `${5 + Math.random() * 90}%`,
  w: 2 + Math.random() * 4,
  dly: Math.random() * 8,
  dur: 6 + Math.random() * 6,
}));

const FlightPath = ({ d, dur, delay, size, op }) => {
  const pathRef = useRef(null);
  const [pos, setPos] = useState({ x: 0, y: 0, angle: 0 });
  const frameRef = useRef(null);
  const startRef = useRef(null);

  useEffect(() => {
    if (!pathRef.current) return;
    const el = pathRef.current;
    const len = el.getTotalLength();
    const animate = (ts) => {
      if (!startRef.current) startRef.current = ts;
      const elapsed = (ts - startRef.current - delay * 1000) % (dur * 1000);
      if (elapsed < 0) { frameRef.current = requestAnimationFrame(animate); return; }
      const t = elapsed / (dur * 1000);
      const pt = el.getPointAtLength(t * len);
      const pt2 = el.getPointAtLength(Math.min(t * len + 2, len));
      const angle = Math.atan2(pt2.y - pt.y, pt2.x - pt.x) * (180 / Math.PI);
      setPos({ x: pt.x, y: pt.y, angle });
      frameRef.current = requestAnimationFrame(animate);
    };
    frameRef.current = requestAnimationFrame(animate);
    return () => cancelAnimationFrame(frameRef.current);
  }, [d, dur, delay]);

  return (
    <g>
      <path ref={pathRef} d={d} fill="none" stroke="rgba(232,146,10,0.12)" strokeWidth="1.5" strokeDasharray="6 8" />
      <path d={d} fill="none" stroke="rgba(232,146,10,0.06)" strokeWidth="8" strokeLinecap="round" />
      <g transform={`translate(${pos.x},${pos.y}) rotate(${pos.angle})`} opacity={op}>
        <g transform={`scale(${size / 14})`}>
          <path d="M14 0 L-4 -6 L-2 0 L-4 6 Z" fill="rgba(232,146,10,0.7)" />
          <path d="M-2 0 L-10 -10 L-8 0 L-10 10 Z" fill="rgba(232,146,10,0.45)" />
          <path d="M-8 0 L-12 -4 L-11 0 L-12 4 Z" fill="rgba(232,146,10,0.35)" />
          <ellipse cx="4" cy="0" rx="5" ry="2" fill="rgba(245,165,30,0.15)" />
        </g>
      </g>
    </g>
  );
};

const CityDot = ({ cx, cy, label, delay }) => (
  <g>
    <circle cx={cx} cy={cy} r="10" fill="rgba(232,146,10,0.06)">
      <animate attributeName="r" values="6;14;6" dur="3s" begin={`${delay}s`} repeatCount="indefinite" />
      <animate attributeName="opacity" values="0.1;0.05;0.1" dur="3s" begin={`${delay}s`} repeatCount="indefinite" />
    </circle>
    <circle cx={cx} cy={cy} r="3" fill="rgba(232,146,10,0.5)">
      <animate attributeName="r" values="2.5;3.5;2.5" dur="2s" begin={`${delay}s`} repeatCount="indefinite" />
    </circle>
    <circle cx={cx} cy={cy} r="1.5" fill="rgba(232,146,10,0.8)" />
    {label && (
      <text x={cx} y={cy - 10} textAnchor="middle" fontSize="8" fill="rgba(160,130,80,0.5)" fontFamily="'Outfit',sans-serif" fontWeight="300" letterSpacing="1.5">{label}</text>
    )}
  </g>
);

const Login = () => {
  const [formData, setFormData] = useState({ email: '', password: '' });
  const [showPassword, setShowPassword] = useState(false);
  const [validationErrors, setValidationErrors] = useState({});
  const [touched, setTouched] = useState({});
  const [focusField, setFocusField] = useState(null);
  const [mounted, setMounted] = useState(false);
  const [mousePos, setMousePos] = useState({ x: 0.5, y: 0.5 });
  const { execute: login, loading, error } = useLogin();

  useEffect(() => {
    const t = setTimeout(() => setMounted(true), 100);
    return () => clearTimeout(t);
  }, []);

  const handleMouse = useCallback((e) => {
    const rect = e.currentTarget.getBoundingClientRect();
    setMousePos({
      x: (e.clientX - rect.left) / rect.width,
      y: (e.clientY - rect.top) / rect.height,
    });
  }, []);

  const handleChange = useCallback((e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    setTouched((prev) => {
      if (!prev[name]) return prev;
      setValidationErrors((errs) => ({ ...errs, [name]: VALIDATORS[name](value) }));
      return prev;
    });
  }, []);

  const handleBlur = useCallback((e) => {
    const { name, value } = e.target;
    setFocusField(null);
    setTouched((prev) => ({ ...prev, [name]: true }));
    setValidationErrors((prev) => ({ ...prev, [name]: VALIDATORS[name](value) }));
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setTouched({ email: true, password: true });
    const errors = {
      email: validateEmail(formData.email),
      password: validatePassword(formData.password),
    };
    setValidationErrors(errors);
    if (errors.email || errors.password) return;
    try {
      localStorage.setItem('justLoggedIn', Date.now().toString());
      localStorage.removeItem('intentionalLogout');
      await login(formData);
    } catch (err) {
      localStorage.removeItem('justLoggedIn');
      console.error('Login failed:', err);
    }
  };

  const togglePasswordVisibility = useCallback(() => setShowPassword((v) => !v), []);

  const emailError = touched.email && validationErrors.email;
  const passwordError = touched.password && validationErrors.password;

  return (
    <div className={styles.root} onMouseMove={handleMouse}>

      <div className={styles.bgLayer}>
        <div
          className={styles.gradientMesh}
          style={{
            background: `
              radial-gradient(ellipse 600px 600px at ${30 + mousePos.x * 10}% ${20 + mousePos.y * 10}%, rgba(255,220,150,0.18) 0%, transparent 70%),
              radial-gradient(ellipse 500px 500px at ${70 - mousePos.x * 8}% ${60 + mousePos.y * 8}%, rgba(232,146,10,0.08) 0%, transparent 60%),
              radial-gradient(ellipse 400px 400px at ${50 + mousePos.x * 5}% ${80 - mousePos.y * 5}%, rgba(200,180,140,0.06) 0%, transparent 50%),
              linear-gradient(165deg, #fffdf8 0%, #fef9f0 30%, #fdf6ea 60%, #fcf3e4 100%)
            `,
          }}
        />
        <div className={styles.gridPattern} />

        <svg className={styles.flightSvg} viewBox="0 0 1000 650" preserveAspectRatio="xMidYMid slice">
          <defs>
            <radialGradient id="glow1" cx="50%" cy="50%" r="50%">
              <stop offset="0%" stopColor="rgba(245,165,30,0.12)" />
              <stop offset="100%" stopColor="rgba(245,165,30,0)" />
            </radialGradient>
          </defs>

          <circle cx="200" cy="150" r="80" fill="url(#glow1)">
            <animate attributeName="cx" values="200;230;200" dur="8s" repeatCount="indefinite" />
            <animate attributeName="cy" values="150;130;150" dur="6s" repeatCount="indefinite" />
          </circle>
          <circle cx="750" cy="300" r="100" fill="url(#glow1)">
            <animate attributeName="cx" values="750;720;750" dur="10s" repeatCount="indefinite" />
            <animate attributeName="cy" values="300;320;300" dur="7s" repeatCount="indefinite" />
          </circle>
          <circle cx="500" cy="450" r="60" fill="url(#glow1)">
            <animate attributeName="cx" values="500;520;500" dur="9s" repeatCount="indefinite" />
          </circle>

          {FLIGHT_PATHS.map((fp, i) => <FlightPath key={i} {...fp} />)}
          {CITIES.map((c, i) => <CityDot key={i} {...c} />)}

          <g className={styles.floatStamp1} opacity="0.06">
            <rect x="95" y="270" width="50" height="60" rx="4" fill="none" stroke="currentColor" strokeWidth="1.5" strokeDasharray="3 2" />
            <text x="120" y="292" textAnchor="middle" fontSize="7" fontWeight="600" letterSpacing="1" fill="currentColor">VISA</text>
            <line x1="105" y1="304" x2="135" y2="304" stroke="currentColor" strokeWidth="0.8" />
            <circle cx="120" cy="282" r="5" fill="none" stroke="currentColor" strokeWidth="0.8" />
          </g>

          <g className={styles.floatStamp2} opacity="0.06">
            <rect x="790" y="162" width="60" height="36" rx="3" fill="none" stroke="currentColor" strokeWidth="1.2" />
            <text x="820" y="176" textAnchor="middle" fontSize="6" fontWeight="700" letterSpacing="2" fill="currentColor">BOARDING</text>
            <text x="820" y="186" textAnchor="middle" fontSize="5" letterSpacing="1" fill="currentColor">PASS</text>
          </g>

          <g className={styles.floatStamp3} opacity="0.06">
            <circle cx="650" cy="530" r="22" fill="none" stroke="currentColor" strokeWidth="1.2" />
            <circle cx="650" cy="530" r="18" fill="none" stroke="currentColor" strokeWidth="0.5" strokeDasharray="2 2" />
            <text x="650" y="533" textAnchor="middle" fontSize="8" fill="currentColor">✈</text>
          </g>
        </svg>

        {PARTICLES.map((p, i) => (
          <div
            key={i}
            className={styles.particle}
            style={{
              left: p.left,
              top: p.top,
              width: `${p.w}px`,
              height: `${p.w}px`,
              animationDelay: `${p.dly}s`,
              animationDuration: `${p.dur}s`,
            }}
          />
        ))}
      </div>

      <div className={`${styles.brandSide} ${mounted ? styles.brandMounted : ''}`}>
        <div className={styles.brandContent}>
          <div className={styles.brandBadge}>
            <span className={styles.brandBadgeIcon}>✈</span>
            <span className={styles.brandBadgeLabel}>SUNSKY</span>
          </div>

          <h1 className={styles.brandTitle}>
            <span className={styles.brandLine1}>Navigate</span>
            <span className={styles.brandLine2}>the world</span>
            <span className={styles.brandLine3}>of travel.</span>
          </h1>

          <p className={styles.brandDesc}>
            Your premium travel management platform — bookings, finance, and operations unified in one powerful dashboard.
          </p>

          <div className={styles.brandStats}>
            {[
              { num: '50+', label: 'Destinations' },
              { num: '10k', label: 'Bookings' },
              { num: '99%', label: 'Uptime' },
            ].map((s, i) => (
              <div key={i} className={styles.brandStat} style={{ animationDelay: `${0.8 + i * 0.15}s` }}>
                <span className={styles.statNum}>{s.num}</span>
                <span className={styles.statLabel}>{s.label}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      <div className={styles.formSide}>
        <div className={`${styles.card} ${mounted ? styles.cardMounted : ''}`}>
          <div className={styles.cardAccent} />
          <div className={styles.cardInner}>

            <div className={styles.logoBlock}>
              <div className={styles.logoCircle}>
                <img src={require('../../assets/new-logo.png')} alt="Sunsky" className={styles.logo} />
              </div>
              <div className={styles.logoText}>
                <span className={styles.logoName}>Sunsky</span>
                <span className={styles.logoDivider} />
                <span className={styles.logoLabel}>Admin</span>
              </div>
            </div>

            <div className={styles.formHeader}>
              <h2 className={styles.formTitle}>Welcome back</h2>
              <p className={styles.formSubtitle}>Sign in to continue to your dashboard</p>
            </div>

            <form onSubmit={handleSubmit} noValidate className={styles.form}>

              <div className={styles.fieldWrap}>
                <label
                  className={`${styles.fieldLabel} ${focusField === 'email' ? styles.fieldLabelActive : ''}`}
                  htmlFor="login-email"
                >
                  Email address
                </label>
                <div className={`${styles.fieldBox} ${focusField === 'email' ? styles.fieldBoxFocus : ''} ${emailError ? styles.fieldBoxErr : ''} ${formData.email && !emailError ? styles.fieldBoxOk : ''}`}>
                  <svg className={styles.fieldIco} viewBox="0 0 20 20" fill="none">
                    <path d="M3 6.5A1.5 1.5 0 0 1 4.5 5h11A1.5 1.5 0 0 1 17 6.5v7a1.5 1.5 0 0 1-1.5 1.5h-11A1.5 1.5 0 0 1 3 13.5v-7Z" stroke="currentColor" strokeWidth="1.3" />
                    <path d="m3.5 6.5 6.5 4.5 6.5-4.5" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" />
                  </svg>
                  <input
                    id="login-email"
                    type="email"
                    name="email"
                    placeholder="you@company.com"
                    value={formData.email}
                    onChange={handleChange}
                    onFocus={() => setFocusField('email')}
                    onBlur={handleBlur}
                    autoComplete="email"
                    required
                    className={styles.inp}
                  />
                  {formData.email && !emailError && (
                    <svg viewBox="0 0 16 16" width="16" fill="none" className={styles.okIco}>
                      <circle cx="8" cy="8" r="7" stroke="#22c55e" strokeWidth="1.3" />
                      <path d="m5 8 2 2 4-4" stroke="#22c55e" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                  )}
                </div>
                {emailError && <span className={styles.errMsg}>{emailError}</span>}
              </div>

              <div className={styles.fieldWrap}>
                <label
                  className={`${styles.fieldLabel} ${focusField === 'password' ? styles.fieldLabelActive : ''}`}
                  htmlFor="login-password"
                >
                  Password
                </label>
                <div className={`${styles.fieldBox} ${focusField === 'password' ? styles.fieldBoxFocus : ''} ${passwordError ? styles.fieldBoxErr : ''} ${formData.password && !passwordError ? styles.fieldBoxOk : ''}`}>
                  <svg className={styles.fieldIco} viewBox="0 0 20 20" fill="none">
                    <rect x="4.5" y="9" width="11" height="7.5" rx="1.2" stroke="currentColor" strokeWidth="1.3" />
                    <path d="M7.5 9V7a2.5 2.5 0 0 1 5 0v2" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" />
                    <circle cx="10" cy="12.8" r="1" fill="currentColor" />
                  </svg>
                  <input
                    id="login-password"
                    type={showPassword ? 'text' : 'password'}
                    name="password"
                    placeholder="••••••••"
                    value={formData.password}
                    onChange={handleChange}
                    onFocus={() => setFocusField('password')}
                    onBlur={handleBlur}
                    autoComplete="current-password"
                    required
                    className={styles.inp}
                  />
                  <button
                    type="button"
                    className={styles.eyeBtn}
                    onClick={togglePasswordVisibility}
                    aria-label={showPassword ? 'Hide password' : 'Show password'}
                  >
                    {showPassword ? <EyeOffIcon /> : <EyeIcon />}
                  </button>
                </div>
                {passwordError && <span className={styles.errMsg}>{passwordError}</span>}
              </div>

              {error && (
                <div className={styles.serverErr}>
                  <svg viewBox="0 0 16 16" width="14" fill="currentColor">
                    <path d="M8 1a7 7 0 1 0 0 14A7 7 0 0 0 8 1Zm0 3.5a.75.75 0 0 1 .75.75v3a.75.75 0 0 1-1.5 0v-3A.75.75 0 0 1 8 4.5Zm0 6.5a.75.75 0 1 1 0-1.5.75.75 0 0 1 0 1.5Z" />
                  </svg>
                  {error}
                </div>
              )}

              <button type="submit" className={styles.btn} disabled={loading}>
                <span className={styles.btnContent}>
                  {loading ? (
                    <><span className={styles.spinner} />Signing in…</>
                  ) : (
                    <>Get Started <svg viewBox="0 0 20 20" width="15" fill="none" className={styles.btnArrow}><path d="M4 10h12M11 5l5 5-5 5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" /></svg></>
                  )}
                </span>
              </button>

            </form>

            <div className={styles.footer}>
              <span>Sunsky Admin</span>
              <span className={styles.footerDot}>·</span>
              <span>Internal use only</span>
            </div>
          </div>
        </div>
      </div>

    </div>
  );
};

export default Login;